/**
 * @file 新增块按钮
 */
import { getTheme } from '@/utils/theme'
import { Dropdown, DropdownItem } from '@seiue/ui'
import { useAtom } from 'jotai'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { BlockStateEnum } from 'packages/feature-utils/test-paper'
import { BlockTypeEnum } from 'packages/sdks-next/growp'
import { FC, useContext } from 'react'
import styled, { css } from 'styled-components'

import { EditPaperContext } from '../pages/EditPaper/context'

import { focusedBlockAtom } from './BlockList/atoms'
import { HandoutBlockListContext } from './BlockList/context'

interface ButtonAddBlockProp {
  className?: string
  /**
   * 块添加的位置，默认为 0
   */
  position?: number
  /**
   * 父节点的 id
   */
  parentId: number
  /**
   * 是否默认隐藏(指 zero opacity)，鼠标 hover 的时候再显示
   */
  autoHide?: boolean
}

const ButtonAddBlockRaw: FC<ButtonAddBlockProp> = ({
  className,
  position = 0,
  parentId,
  autoHide = true,
}) => {
  const {
    api: { createBlock },
  } = useContext(HandoutBlockListContext)
  const { isRand, limitLevel } = useContext(EditPaperContext)
  const theme = getTheme()

  const [focusedBlock, setFocusedBlock] = useAtom(focusedBlockAtom)

  const onChange = async (item: DropdownItem) => {
    const { data: block } = await createBlock(
      position < 0 ? 0 : position,
      item.value,
      parentId
    )
    setFocusedBlock({
      id: block.id,
      state: BlockStateEnum.Editing,
    })
  }

  const disabled =
    focusedBlock.id > 0 && focusedBlock.state === BlockStateEnum.Editing
  return (
    <ButtonWrapper
      className={`${className} add-line-wrapper`}
      $autoHide={autoHide}
      $disabled={disabled}
      onClick={() => {
        if (isRand) {
          onChange({
            label: '新增题目',
            value: BlockTypeEnum.Question,
          })
        }
      }}
    >
      <Dropdown
        items={
          isRand || disabled
            ? []
            : [
                {
                  label: '新增标题',
                  value: BlockTypeEnum.FirstLevelTitle,
                },
                {
                  label: '新增资料',
                  value: BlockTypeEnum.Fulltext,
                },
                ...(limitLevel > 1
                  ? []
                  : [
                      {
                        label: '新增题目',
                        value: BlockTypeEnum.Question,
                      },
                    ]),
              ]
        }
        onChange={onChange}
        placement="bottomLeft"
        trigger="hover"
      >
        <GrowpIcon
          name="AddWithBorder"
          size={24}
          color={theme.brand._1}
          style={{
            cursor: 'pointer',
          }}
        />
      </Dropdown>
      <LineDashed />
    </ButtonWrapper>
  )
}

const ButtonWrapper = styled.button<{ $autoHide: boolean; $disabled: boolean }>`
  border: none;
  background-color: transparent;
  width: 100%;
  height: 24px;
  line-height: 0;
  padding: 0;
  display: flex;
  align-items: center;

  ${({ $autoHide, $disabled }) =>
    $autoHide || $disabled
      ? css`
          opacity: 0;
          &&& {
            &:hover {
              opacity: ${$disabled ? 0 : 1};
            }
          }
        `
      : undefined}
`

const LineDashed = styled.div`
  flex: 1;
  height: 0px;
  border-bottom: 1px dashed ${(props) => props.theme.border.greyDark};
`

export const ButtonAddBlock = styled(ButtonAddBlockRaw)`
  color: ${(props) => props.theme.border.greyDark};

  &:hover {
    color: ${(props) => props.theme.brand._1};

    ${LineDashed} {
      border-color: ${(props) => props.theme.brand._1};
    }
  }
`
